import { Box, Button, Image, Link, Text, useToast } from '@chakra-ui/react';
import { BrokerType, brokerTypesData } from 'enums/shared/brokerType';
import { Form, Formik } from 'formik';
import useBrokerLogo from 'hooks/modules/brokerAccount/useBrokerLogo';
import useFormError from 'hooks/shared/useFormError';
import ConnectBrokerInstructionItem from 'presentation/components/modules/brokerAccount/create/ConnectBrokerInstructionItem';
import HintScreenButton from 'presentation/components/modules/brokerAccount/create/HintScreenButton';
import AppCard from 'presentation/components/shared/card/AppCard';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import SettingsLayout from 'presentation/layout/modules/settings/SettingsLayout';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { useCreateBrokerAccountMutation } from 'rtkQuery/sharespro/queries/brokerAccounts';
import * as Yup from 'yup';

import binance2EnScreen from 'assets/img/instructions/connect_binance/binance_2_en.png';
import binance2RuScreen from 'assets/img/instructions/connect_binance/binance_2_ru.png';

import binance3EnScreen from 'assets/img/instructions/connect_binance/binance_3_en.png';
import binance3RuScreen from 'assets/img/instructions/connect_binance/binance_3_ru.png';

import binance4EnScreen from 'assets/img/instructions/connect_binance/binance_4_en.png';
import binance4RuScreen from 'assets/img/instructions/connect_binance/binance_4_ru.png';

import binance5EnScreen from 'assets/img/instructions/connect_binance/binance_5_en.png';
import binance5RuScreen from 'assets/img/instructions/connect_binance/binance_5_ru.png';

import binance6EnScreen from 'assets/img/instructions/connect_binance/binance_6_en.png';
import binance6RuScreen from 'assets/img/instructions/connect_binance/binance_6_ru.png';

import binance7EnScreen from 'assets/img/instructions/connect_binance/binance_7_en.png';
import binance7RuScreen from 'assets/img/instructions/connect_binance/binance_7_ru.png';

import binance8EnScreen from 'assets/img/instructions/connect_binance/binance_8_en.png';
import binance8RuScreen from 'assets/img/instructions/connect_binance/binance_8_ru.png';

interface FormValues {
  public_key: string;
  secret_key: string;
}

const ConnectBinanceBrokerPage: FC = () => {
  const { t, i18n } = useTranslation();

  const brokerLogo = useBrokerLogo({ isFullLogo: true });

  const [createBroker, { isLoading }] = useCreateBrokerAccountMutation();

  const navigate = useNavigate();

  const formErrorHandler = useFormError<FormValues>();

  const toast = useToast();

  const validationSchema = Yup.object().shape({
    public_key: Yup.string().required(t('fieldIsRequired') ?? ''),
    secret_key: Yup.string().required(t('fieldIsRequired') ?? ''),
  });

  const title = t('connectBinance');

  return (
    <SettingsLayout>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <GridHeading>{title}</GridHeading>
      <AppCard p="40px" mb="20px" w="100%">
        <Image
          src={brokerLogo({ brokerType: BrokerType.Binance })}
          w="200px"
          mb="20px"
          ml="120px"
        />
        <Formik<FormValues>
          initialValues={{
            public_key: '',
            secret_key: '',
          }}
          onSubmit={(values, formik) => {
            createBroker({
              broker_id: brokerTypesData[BrokerType.Binance].id,
              public_key: values.public_key,
              secret_key: values.secret_key,
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('brokerAccountWasCreated'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.portfolio);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
          validationSchema={validationSchema}
        >
          {({ handleChange, values }) => {
            const steps = [
              <Text key={0}>
                {t('binanceBrokerConnectStep1_1')}
                <Link
                  href={
                    i18n.language === 'ru'
                      ? 'https://www.binance.com/ru'
                      : 'https://www.binance.com/en'
                  }
                  textDecoration="underline"
                  isExternal
                >
                  {t('binanceBrokerConnectStep1_2')}
                </Link>
              </Text>,
              <Box key={1}>
                <Text mb="10px">{t('binanceBrokerConnectStep2')}</Text>
                <HintScreenButton
                  ruPhotos={[binance2RuScreen]}
                  enPhotos={[binance2EnScreen]}
                />
              </Box>,
              <Box key={2}>
                <Text mb="10px">
                  {t('binanceBrokerConnectStep3_1')}
                  <Link
                    href={
                      i18n.language === 'ru'
                        ? 'https://www.binance.com/ru/support/faq/функции-аккаунта?c=1&navId=1#11'
                        : 'https://www.binance.com/en/support/faq/account-functions?c=1&navId=1#11'
                    }
                    textDecoration="underline"
                    isExternal
                  >
                    {t('binanceBrokerConnectStep3_2')}
                  </Link>
                  {t('binanceBrokerConnectStep3_3')}
                </Text>
                <HintScreenButton
                  ruPhotos={[binance3RuScreen]}
                  enPhotos={[binance3EnScreen]}
                />
              </Box>,
              <Box key={3}>
                <Text mb="10px">{t('binanceBrokerConnectStep4')}</Text>
                <HintScreenButton
                  ruPhotos={[binance4RuScreen]}
                  enPhotos={[binance4EnScreen]}
                />
              </Box>,
              <Box key={4}>
                <Text mb="10px">{t('binanceBrokerConnectStep5')}</Text>
                <HintScreenButton
                  ruPhotos={[binance5RuScreen]}
                  enPhotos={[binance5EnScreen]}
                />
              </Box>,
              <Box key={5}>
                <Text mb="10px">{t('binanceBrokerConnectStep6')}</Text>
                <HintScreenButton
                  ruPhotos={[binance6RuScreen]}
                  enPhotos={[binance6EnScreen]}
                />
              </Box>,
              <Box key={6}>
                <Text mb="10px">{t('binanceBrokerConnectStep7')}</Text>
                <HintScreenButton
                  ruPhotos={[binance7RuScreen]}
                  enPhotos={[binance7EnScreen]}
                />
              </Box>,
              <Box key={7}>
                <Text mb="10px">{t('binanceBrokerConnectStep8')}</Text>
                <HintScreenButton
                  ruPhotos={[binance8RuScreen]}
                  enPhotos={[binance8EnScreen]}
                />
              </Box>,
              <Box key={8} pt="10px">
                <Text mb="40px">{t('binanceBrokerConnectStep9')}</Text>
                <FormTextInput
                  name="public_key"
                  onChange={handleChange}
                  value={values.public_key}
                  w="364px"
                  placeholder={t('binanceApiKeyField') ?? ''}
                  autoComplete="off"
                />
                <Box key={10} pt="35px">
                  <FormTextInput
                    name="secret_key"
                    w="364px"
                    onChange={handleChange}
                    value={values.secret_key}
                    type="password"
                    placeholder={t('binanceSecretKeyField') ?? ''}
                    autoComplete="off"
                  />
                </Box>
              </Box>,
            ];

            return (
              <Form>
                <Box position="relative" w="100%" mb="35px">
                  <Box w="100%">
                    {steps.map((stepText, index) => (
                      <Box key={index} mb="25px">
                        <ConnectBrokerInstructionItem
                          hasFormFields={index > 7}
                          step={index + 1}
                        >
                          {stepText}
                        </ConnectBrokerInstructionItem>
                      </Box>
                    ))}
                  </Box>
                  <Box
                    position="absolute"
                    left="78px"
                    top="0"
                    bgColor={Color.Shade100}
                    w="2px"
                    h="100%"
                  />
                </Box>
                <Button ml="120px" type="submit" isLoading={isLoading}>
                  {t('continuePhrase')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </SettingsLayout>
  );
};

export default ConnectBinanceBrokerPage;
